import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IStokasticPropsState, PlayerCustomization, PlayerRates } from './types';
import { BetSlipItem, BetType, DisplayGame, LockedTeaser, SortDirection, SortField } from '../data/types';
import { CalculateEVResponse } from '../data/evTypes';
import { DEFAULT_PROPS_LEAGUE, leagueHasMinutes, leagueHasPositionFilter } from '../data/leagueConfig';

const STORAGE_KEY = 'stokastic_props_filters';
const CUSTOMIZATIONS_STORAGE_KEY = 'stokastic_props_customizations';
const LEAGUE_WEIGHTS_STORAGE_KEY = 'stokastic_props_league_weights';

// Filters to persist in localStorage
interface PersistedFilters {
  league?: string;
  state?: string;
  selectedSportsbooks?: string[];
  minEdge?: number;
  minMinutes?: number;
  maxEV?: number;
  minOdds?: number;
  maxOdds?: number;
  hideInjuredTeammate?: boolean;
  requireBothModelsEV?: boolean;
  positionFilter?: string;
}

type PersistedLeagueWeights = Record<string, { stokWeight: number; marketWeight: number }>;

// Player customizations to persist in localStorage
interface PersistedCustomizations {
  savedDate: string;  // ISO date string (YYYY-MM-DD) - customizations expire at midnight
  playerCustomizations: Record<string, PlayerCustomization>;
}

// Load persisted filters from localStorage
const loadPersistedFilters = (): PersistedFilters => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return {};
};

// Save filters to localStorage
const saveFiltersToStorage = (filters: PersistedFilters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
};

const loadLeagueWeights = (): PersistedLeagueWeights => {
  try {
    const stored = localStorage.getItem(LEAGUE_WEIGHTS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return {};
};

const saveLeagueWeight = (league: string, stokWeight: number, marketWeight: number) => {
  try {
    const current = loadLeagueWeights();
    current[league] = { stokWeight, marketWeight };
    localStorage.setItem(LEAGUE_WEIGHTS_STORAGE_KEY, JSON.stringify(current));
  } catch {
    // ignore
  }
};

// Get today's date as YYYY-MM-DD string
const getTodayDateString = (): string => new Date().toISOString().split('T')[0];

// Load persisted customizations from localStorage
// Returns empty if saved date doesn't match today (customizations expire at midnight)
const loadPersistedCustomizations = (): PersistedCustomizations => {
  const today = getTodayDateString();
  try {
    const stored = localStorage.getItem(CUSTOMIZATIONS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as PersistedCustomizations;
      // Check if customizations are from today - if not, they've expired
      if (parsed.savedDate !== today) {
        localStorage.removeItem(CUSTOMIZATIONS_STORAGE_KEY);
        return { savedDate: today, playerCustomizations: {} };
      }
      return parsed;
    }
  } catch {
    // ignore
  }
  return { savedDate: today, playerCustomizations: {} };
};

// Save customizations to localStorage (always saves with today's date)
const saveCustomizationsToStorage = (playerCustomizations: Record<string, PlayerCustomization>) => {
  try {
    const data: PersistedCustomizations = {
      savedDate: getTodayDateString(),
      playerCustomizations,
    };
    localStorage.setItem(CUSTOMIZATIONS_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

// Get current persisted filters from state
const getPersistedFiltersFromState = (state: IStokasticPropsState): PersistedFilters => ({
  league: state.filters.league,
  state: state.filters.state,
  selectedSportsbooks: state.filters.selectedSportsbooks,
  minEdge: state.filters.minEdge,
  minMinutes: state.filters.minMinutes,
  maxEV: state.filters.maxEV,
  minOdds: state.filters.minOdds,
  maxOdds: state.filters.maxOdds,
  hideInjuredTeammate: state.filters.hideInjuredTeammate,
  requireBothModelsEV: state.filters.requireBothModelsEV,
  positionFilter: state.filters.positionFilter,
});

const getDefaultWeightsForLeague = (league: string): { stokWeight: number; marketWeight: number } => {
  return { stokWeight: 0.5, marketWeight: 0.5 };
};

const getWeightsForLeague = (league: string, stored: PersistedLeagueWeights) =>
  stored[league] ?? getDefaultWeightsForLeague(league);

// Default filter values
const defaultFilters = {
  league: DEFAULT_PROPS_LEAGUE,
  state: 'IL',
  selectedSportsbooks: [] as string[],
  selectedStatFilter: 'All',
  positionFilter: 'All',
  minLineFilter: '',
  minEdge: 0,
  minMinutes: 10,
  stokWeight: 0.5,
  marketWeight: 0.5,
  playerSearch: '',
  maxEV: 0,
  minOdds: 0,
  maxOdds: 0,
  hideInjuredTeammate: false,
  requireBothModelsEV: false,
};

// Merge persisted filters with defaults
const persisted = loadPersistedFilters();
const persistedCustomizations = loadPersistedCustomizations();

const activeLeague = persisted.league ?? defaultFilters.league;
const { stokWeight: initialStokWeight, marketWeight: initialMarketWeight } =
  getWeightsForLeague(activeLeague, loadLeagueWeights());

const initialState: IStokasticPropsState = {
  filters: {
    ...defaultFilters,
    league: activeLeague,
    state: persisted.state ?? defaultFilters.state,
    selectedSportsbooks: persisted.selectedSportsbooks ?? defaultFilters.selectedSportsbooks,
    minEdge: persisted.minEdge ?? defaultFilters.minEdge,
    minMinutes: persisted.minMinutes ?? defaultFilters.minMinutes,
    stokWeight: initialStokWeight,
    marketWeight: initialMarketWeight,
    maxEV: persisted.maxEV ?? defaultFilters.maxEV,
    minOdds: persisted.minOdds ?? defaultFilters.minOdds,
    maxOdds: persisted.maxOdds ?? defaultFilters.maxOdds,
    // Minutes-based filter: never hydrate it on for a league without projected minutes (its control is hidden there)
    hideInjuredTeammate: leagueHasMinutes(activeLeague) ? persisted.hideInjuredTeammate ?? defaultFilters.hideInjuredTeammate : false,
    requireBothModelsEV: persisted.requireBothModelsEV ?? defaultFilters.requireBothModelsEV,
    // A position the current feed doesn't offer is normalized to All by the selects
    positionFilter: leagueHasPositionFilter(activeLeague) ? persisted.positionFilter ?? defaultFilters.positionFilter : 'All',
  },
  games: [],
  lockedTeaser: null,
  selectedGameIds: [],
  expandedPlayerId: null,
  betSlip: [],
  betType: 'STRAIGHT',
  sortField: 'evPct',
  sortDirection: 'desc',
  currentPage: 1,
  isLoading: false,
  lastUpdatedTime: null,
  lastUpdatedTimestamp: null,
  error: null,
  playerCustomizations: persistedCustomizations.playerCustomizations ?? {},
  playerCustomEVData: {},
  projectionsUpdatedSinceCustomization: false,
};

const stokasticPropsSlice = createSlice({
  name: 'stokasticProps',
  initialState,
  reducers: {
    setLeagueFilter: (state, action: PayloadAction<string>) => {
      state.filters.league = action.payload;
      // Clear matchup selection and reset to Top Bets view when switching leagues
      state.selectedGameIds = [];
      state.expandedPlayerId = null;
      state.currentPage = 1;
      // Reset stat/position filters since they differ between leagues
      state.filters.selectedStatFilter = 'All';
      state.filters.positionFilter = 'All';
      // The injured-teammate filter relies on projected minutes; leagues without them hide the control
      if (!leagueHasMinutes(action.payload)) state.filters.hideInjuredTeammate = false;
      // Clear games data to prevent showing old league data during subscription check
      state.games = [];
      state.lockedTeaser = null;
      // Clear update time so the previous league's timestamp doesn't show while loading
      state.lastUpdatedTime = null;
      state.lastUpdatedTimestamp = null;
      // Clear any custom projections from previous league
      state.playerCustomEVData = {};
      state.playerCustomizations = {};
      // Load per-league weights (or league default if never customized)
      const { stokWeight, marketWeight } = getWeightsForLeague(action.payload, loadLeagueWeights());
      state.filters.stokWeight = stokWeight;
      state.filters.marketWeight = marketWeight;
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    setStateFilter: (state, action: PayloadAction<string>) => {
      state.filters.state = action.payload;
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    setSelectedSportsbooks: (state, action: PayloadAction<string[]>) => {
      state.filters.selectedSportsbooks = action.payload;
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    toggleSportsbook: (state, action: PayloadAction<string>) => {
      const book = action.payload;
      const index = state.filters.selectedSportsbooks.indexOf(book);
      if (index > -1) {
        state.filters.selectedSportsbooks.splice(index, 1);
      } else {
        state.filters.selectedSportsbooks.push(book);
      }
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    clearSportsbooks: (state) => {
      state.filters.selectedSportsbooks = [];
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    setSelectedStatFilter: (state, action: PayloadAction<string>) => {
      state.filters.selectedStatFilter = action.payload;
      state.currentPage = 1;
    },
    setPositionFilter: (state, action: PayloadAction<string>) => {
      // Position filtering exists for NFL only; other leagues always stay on All
      state.filters.positionFilter = leagueHasPositionFilter(state.filters.league) ? action.payload : 'All';
      state.currentPage = 1;
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    setMinLineFilter: (state, action: PayloadAction<string>) => {
      state.filters.minLineFilter = action.payload;
      state.currentPage = 1;
    },
    setMinMinutes: (state, action: PayloadAction<number>) => {
      state.filters.minMinutes = action.payload;
      state.currentPage = 1;
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    setPlayerSearch: (state, action: PayloadAction<string>) => {
      state.filters.playerSearch = action.payload;
      state.currentPage = 1;
    },
    setStokWeight: (state, action: PayloadAction<number>) => {
      state.filters.stokWeight = action.payload;
      saveLeagueWeight(state.filters.league, action.payload, state.filters.marketWeight);
    },
    setMarketWeight: (state, action: PayloadAction<number>) => {
      state.filters.marketWeight = action.payload;
      saveLeagueWeight(state.filters.league, state.filters.stokWeight, action.payload);
    },
    setProjectionWeights: (state, action: PayloadAction<{ stokWeight: number; marketWeight: number }>) => {
      state.filters.stokWeight = action.payload.stokWeight;
      state.filters.marketWeight = action.payload.marketWeight;
      saveLeagueWeight(state.filters.league, action.payload.stokWeight, action.payload.marketWeight);
    },
    setGames: (state, action: PayloadAction<DisplayGame[]>) => {
      state.games = action.payload;
    },
    setLockedTeaser: (state, action: PayloadAction<LockedTeaser | null>) => {
      state.lockedTeaser = action.payload;
    },
    setSelectedGameIds: (state, action: PayloadAction<string[]>) => {
      state.selectedGameIds = action.payload;
      state.expandedPlayerId = null;
    },
    setExpandedPlayerId: (state, action: PayloadAction<string | null>) => {
      state.expandedPlayerId = action.payload;
    },
    toggleExpandedPlayer: (state, action: PayloadAction<string>) => {
      if (state.expandedPlayerId === action.payload) {
        state.expandedPlayerId = null;
      } else {
        state.expandedPlayerId = action.payload;
      }
    },
    addToBetSlip: (state, action: PayloadAction<BetSlipItem>) => {
      const exists = state.betSlip.some((item) => item.id === action.payload.id);
      if (!exists) {
        state.betSlip.push(action.payload);
      }
    },
    removeFromBetSlip: (state, action: PayloadAction<number>) => {
      state.betSlip.splice(action.payload, 1);
    },
    clearBetSlip: (state) => {
      state.betSlip = [];
    },
    setBetType: (state, action: PayloadAction<BetType>) => {
      state.betType = action.payload;
    },
    setSortField: (state, action: PayloadAction<SortField>) => {
      if (state.sortField === action.payload) {
        state.sortDirection = state.sortDirection === 'desc' ? 'asc' : 'desc';
      } else {
        state.sortField = action.payload;
        state.sortDirection = 'desc';
      }
    },
    setSortDirection: (state, action: PayloadAction<SortDirection>) => {
      state.sortDirection = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    nextPage: (state) => {
      state.currentPage += 1;
    },
    prevPage: (state) => {
      if (state.currentPage > 1) {
        state.currentPage -= 1;
      }
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setLastUpdatedTime: (state, action: PayloadAction<string | null>) => {
      state.lastUpdatedTime = action.payload;
    },
    setLastUpdatedTimestamp: (state, action: PayloadAction<number | null>) => {
      state.lastUpdatedTimestamp = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setDrawerFilters: (state, action: PayloadAction<{
      minEdge: number;
      maxEV: number;
      minOdds: number;
      maxOdds: number;
      hideInjuredTeammate: boolean;
      requireBothModelsEV: boolean;
      positionFilter?: string;
    }>) => {
      const p = action.payload;
      if (!leagueHasPositionFilter(state.filters.league)) {
        state.filters.positionFilter = 'All';
      } else if (p.positionFilter !== undefined) {
        state.filters.positionFilter = p.positionFilter;
      }
      state.filters.minEdge = p.minEdge;
      state.filters.maxEV = p.maxEV;
      state.filters.minOdds = p.minOdds;
      state.filters.maxOdds = p.maxOdds;
      // Minutes-based filter: a drawer left open across a league switch can't re-enable it for NFL/MLB
      state.filters.hideInjuredTeammate = leagueHasMinutes(state.filters.league) && p.hideInjuredTeammate;
      state.filters.requireBothModelsEV = p.requireBothModelsEV;
      state.currentPage = 1;
      saveFiltersToStorage(getPersistedFiltersFromState(state));
    },
    resetFilters: (state) => {
      const league = state.filters.league;
      const weights = getDefaultWeightsForLeague(league);
      state.filters = { ...defaultFilters, league, ...weights };
      state.currentPage = 1;
      // Clear persisted filters and this league's stored weights
      try {
        localStorage.removeItem(STORAGE_KEY);
        const leagueWeights = loadLeagueWeights();
        delete leagueWeights[league];
        localStorage.setItem(LEAGUE_WEIGHTS_STORAGE_KEY, JSON.stringify(leagueWeights));
      } catch {
      }
    },
    // Player customization actions
    setPlayerCustomization: (
      state,
      action: PayloadAction<{ playerId: string; customization: PlayerCustomization }>
    ) => {
      state.playerCustomizations[action.payload.playerId] = action.payload.customization;
      state.projectionsUpdatedSinceCustomization = false;
      saveCustomizationsToStorage(state.playerCustomizations);
    },
    setPlayerMinutes: (
      state,
      action: PayloadAction<{ playerId: string; gameId: string; eventStartTime: string; minutes: number; rates: PlayerRates }>
    ) => {
      const { playerId, gameId, eventStartTime, minutes, rates } = action.payload;
      state.playerCustomizations[playerId] = { gameId, eventStartTime, minutes, rates };
      state.projectionsUpdatedSinceCustomization = false;
      saveCustomizationsToStorage(state.playerCustomizations);
    },
    setPlayerRate: (
      state,
      action: PayloadAction<{ playerId: string; stat: keyof PlayerRates; rate: number }>
    ) => {
      const existing = state.playerCustomizations[action.payload.playerId];
      if (existing) {
        existing.rates[action.payload.stat] = action.payload.rate;
        state.projectionsUpdatedSinceCustomization = false;
        saveCustomizationsToStorage(state.playerCustomizations);
      }
    },
    resetPlayerCustomization: (state, action: PayloadAction<string>) => {
      delete state.playerCustomizations[action.payload];
      saveCustomizationsToStorage(state.playerCustomizations);
    },
    resetGameCustomizations: (state, action: PayloadAction<string[]>) => {
      // Reset customizations and custom EV data for all players in the given player IDs list
      action.payload.forEach((playerId) => {
        delete state.playerCustomizations[playerId];
        delete state.playerCustomEVData[playerId];
      });
      saveCustomizationsToStorage(state.playerCustomizations);
    },
    resetAllCustomizations: (state) => {
      state.playerCustomizations = {};
      state.playerCustomEVData = {};
      state.projectionsUpdatedSinceCustomization = false;
      saveCustomizationsToStorage({});
    },
    setProjectionsUpdatedSinceCustomization: (state, action: PayloadAction<boolean>) => {
      state.projectionsUpdatedSinceCustomization = action.payload;
    },
    // Custom EV data actions
    setPlayerCustomEVData: (
      state,
      action: PayloadAction<{ playerId: string; data: CalculateEVResponse }>
    ) => {
      state.playerCustomEVData[action.payload.playerId] = action.payload.data;

      // Update existing betslip items for this player with new EV data
      const { playerId, data } = action.payload;
      state.betSlip.forEach((bet) => {
        // Check if this bet belongs to the player (bet.id format: playerId-stat-book-side)
        if (bet.id.startsWith(`${playerId}-`)) {
          // Find matching outcome in custom EV data
          const outcome = data.outcomes?.find(
            (o) => o.statType === bet.stat && o.label === bet.side
          );
          if (outcome) {
            const sbResult = outcome.sportsbooks.find((sb) => sb.code === bet.book);
            if (sbResult) {
              // Update EV percentage (API returns decimal, convert to percentage)
              if (sbResult.blendedEV != null) {
                bet.evPct = sbResult.blendedEV * 100;
              }
              // Update model win probability
              if (sbResult.blendedTrueWinProbability != null) {
                bet.modelWin = sbResult.blendedTrueWinProbability;
              }
            }
          }
        }
      });
    },
    clearPlayerCustomEVData: (state, action: PayloadAction<string>) => {
      delete state.playerCustomEVData[action.payload];
    },
    clearAllCustomEVData: (state) => {
      state.playerCustomEVData = {};
    },
    // Update betslip items for a player with new projection values
    updateBetSlipProjections: (
      state,
      action: PayloadAction<{
        playerId: string;
        projections: { pts: number; reb: number; ast: number; threes: number; pra: number };
        /** Already-formatted projection per stat (key: stat lower-cased with spaces/-/_ removed). Covers NHL/NFL and any market. */
        projectionsByStat?: Record<string, string>;
      }>
    ) => {
      const { playerId, projections, projectionsByStat } = action.payload;

      state.betSlip.forEach((bet) => {
        // Check if this bet belongs to the player (bet.id format: playerId-stat-book-side)
        if (bet.id.startsWith(`${playerId}-`)) {
          const byStat = projectionsByStat?.[bet.stat.toLowerCase().replace(/[\s\-_]/g, '')];
          if (byStat !== undefined) {
            bet.projection = byStat;
            return;
          }
          // Calculate projection based on stat type
          let newProjection: number | null = null;
          const statUpper = bet.stat.toUpperCase();

          if (statUpper === 'POINTS' || statUpper === 'PTS') {
            newProjection = projections.pts;
          } else if (statUpper === 'REBOUNDS' || statUpper === 'REB') {
            newProjection = projections.reb;
          } else if (statUpper === 'ASSISTS' || statUpper === 'AST') {
            newProjection = projections.ast;
          } else if (statUpper === '3-POINTERS' || statUpper === '3PM' || statUpper === 'THREES') {
            newProjection = projections.threes;
          } else if (statUpper === 'PTS_REBS_ASTS' || statUpper === 'PRA') {
            newProjection = projections.pra;
          } else if (statUpper === 'P+R' || statUpper === 'PR') {
            newProjection = projections.pts + projections.reb;
          } else if (statUpper === 'P+A' || statUpper === 'PA') {
            newProjection = projections.pts + projections.ast;
          } else if (statUpper === 'R+A' || statUpper === 'RA') {
            newProjection = projections.reb + projections.ast;
          }

          if (newProjection !== null) {
            bet.projection = newProjection.toFixed(1);
          }
        }
      });
    },
  },
});

export const {
  setLeagueFilter,
  setStateFilter,
  setSelectedSportsbooks,
  toggleSportsbook,
  clearSportsbooks,
  setSelectedStatFilter,
  setPositionFilter,
  setMinLineFilter,
  setMinMinutes,
  setPlayerSearch,
  setStokWeight,
  setMarketWeight,
  setProjectionWeights,
  setGames,
  setLockedTeaser,
  setSelectedGameIds,
  setExpandedPlayerId,
  toggleExpandedPlayer,
  addToBetSlip,
  removeFromBetSlip,
  clearBetSlip,
  setBetType,
  setSortField,
  setSortDirection,
  setCurrentPage,
  nextPage,
  prevPage,
  setIsLoading,
  setLastUpdatedTime,
  setLastUpdatedTimestamp,
  setError,
  resetFilters,
  setDrawerFilters,
  // Player customization actions
  setPlayerCustomization,
  setPlayerMinutes,
  setPlayerRate,
  resetPlayerCustomization,
  resetGameCustomizations,
  resetAllCustomizations,
  setProjectionsUpdatedSinceCustomization,
  // Custom EV data actions
  setPlayerCustomEVData,
  clearPlayerCustomEVData,
  clearAllCustomEVData,
  // Betslip projection update
  updateBetSlipProjections,
} = stokasticPropsSlice.actions;

export default stokasticPropsSlice.reducer;
